import React, { useCallback, useEffect, useState } from 'react';

import { Badge, Group, Text, useMantineTheme } from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { IconCopy, IconEdit, IconEye, IconTrashX } from '@tabler/icons-react';
import Menu from 'components/Menu';
import Table from 'components/Table';
import useCreateWorkoutMovement from 'hooks/workoutMovement/useCreateWorkoutMovement';
import useWorkoutMovements from 'hooks/workoutMovement/useWorkoutMovements';
import { CellProps } from 'react-table';
import { WorkoutMovementList, WorkoutMovementWrite } from 'types';
import { MovementTypeEnum, SCORE_TYPE_LABEL_MAP, ScoreTypeEnum } from 'types/enums';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

import ModalMovementDelete from './ModalMovementDelete';
import ModalMovementForm from './ModalMovementForm';
import ModalMovementView from './ModalMovementView';

interface IMovementsTableContainerProps {
  permission?: boolean;
  search?: string;
  source?: string;
}

const MovementsTableContainer = ({
  permission = true,
  search,
  source,
}: IMovementsTableContainerProps): JSX.Element => {
  const theme = useMantineTheme();
  const [ordering, setOrdering] = useState('name');
  const [ascending, setAscending] = useState(true);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useWorkoutMovements({
      ordering: ascending ? ordering : `-${ordering}`,
      search,
      source,
    });

  const [selected, setSelected] = useState<WorkoutMovementList | undefined>();
  const [tableData, setTableData] = useState<WorkoutMovementList[]>([]);

  const [isViewOpened, { open: openView, close: closeView }] = useDisclosure(false);
  const [isEditFormOpened, { open: openEditForm, close: closeEditForm }] = useDisclosure(false);
  const [debouncedIsEditFormOpened] = useDebouncedValue(isEditFormOpened, 0);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);

  const createMutation = useCreateWorkoutMovement();
  const { successNotification, errorNotification } = notificationHandler();

  const handleHeaderClick = useCallback(
    (header: string): void => {
      setOrdering(header);
      setAscending(!ascending);
    },
    [ascending],
  );

  const handleView = (movement: WorkoutMovementList): void => {
    setSelected(movement);
    openView();
  };

  const handleEdit = (movement: WorkoutMovementList): void => {
    setSelected(movement);
    openEditForm();
  };

  const handleCloseEdit = (): void => {
    setSelected(undefined);
    closeEditForm();
  };

  const handleDelete = (movement: WorkoutMovementList): void => {
    setSelected(movement);
    openDeleteModal();
  };

  const handleDuplicate = (movement: WorkoutMovementList): void => {
    const payload: WorkoutMovementWrite = {
      workoutMovementId: uuidv4(),
      name: `${movement.name} (copy)`,
      description: movement.description,
      defaultScoreType: movement.defaultScoreType,
      movementType: MovementTypeEnum.MOVEMENT,
    };

    createMutation.mutate(payload, {
      onSuccess: () => {
        successNotification('Movement duplicated', `${payload.name} has been created successfully.`);
      },
      onError: error => {
        const errorMessage = error.response?.data?.message || 'Failed to duplicate movement';
        errorNotification('Duplication failed', errorMessage);
      },
    });
  };

  const columns = [
    {
      Header: 'Movement Name',
      accessor: 'name' as const,
      Cell: ({ row }: CellProps<WorkoutMovementList>) => (
        <Text
          onClick={() => handleEdit(row.original)}
          weight='bold'
          underline
          sx={{ cursor: 'pointer', width: 'fit-content' }}
        >
          {row.original.name}
        </Text>
      ),
    },
    {
      Header: 'Scoring Type',
      accessor: 'defaultScoreType' as const,
      Cell: ({ row }: CellProps<WorkoutMovementList>) => {
        const value = row.original.defaultScoreType;
        if (!value) {
          return (
            <Text color='dimmed' size='sm'>
              —
            </Text>
          );
        }
        return <Text size='sm'>{SCORE_TYPE_LABEL_MAP[value as ScoreTypeEnum]}</Text>;
      },
    },
    {
      Header: 'Source',
      accessor: 'source' as const,
      Cell: ({ row }: CellProps<WorkoutMovementList>) => {
        const isDefault = row.original.source === 'Default';
        return (
          <Badge color={isDefault ? 'gray' : 'blue'} variant='filled'>
            {row.original.source}
          </Badge>
        );
      },
    },
    {
      Header: 'Actions',
      id: 'action',
      Cell: ({ row }: CellProps<WorkoutMovementList>) => {
        // Default movements are system-seeded — Delete stays visible but disabled.
        const isDefault = row.original.source === 'Default';
        return (
          <Group position='right' px='xs'>
            <Menu
              position='bottom-end'
              disabled={!permission}
              items={[
                {
                  title: 'View',
                  onClick: () => handleView(row.original),
                  icon: <IconEye size={theme.fontSizes.sm} />,
                },
                {
                  title: 'Edit',
                  onClick: () => handleEdit(row.original),
                  icon: <IconEdit size={theme.fontSizes.sm} />,
                },
                {
                  title: 'Duplicate',
                  onClick: () => handleDuplicate(row.original),
                  icon: <IconCopy size={theme.fontSizes.sm} />,
                },
                {
                  title: 'Delete',
                  onClick: isDefault ? undefined : () => handleDelete(row.original),
                  icon: <IconTrashX size={theme.fontSizes.sm} />,
                  color: isDefault ? undefined : 'red',
                  disabled: isDefault,
                  rightSection: isDefault ? (
                    <Text size='xs' color='dimmed' ml='xs'>
                      (Cannot delete default)
                    </Text>
                  ) : undefined,
                },
              ]}
            />
          </Group>
        );
      },
      minWidth: '50px',
      maxWidth: '50px',
      sticky: true,
    },
  ];

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data]);

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['name', 'source']}
        handleHeaderClick={handleHeaderClick}
        ordering={ordering}
        ascending={ascending}
        margin={12}
        bgc='white'
      />

      <ModalMovementView
        opened={isViewOpened}
        closeHandler={closeView}
        movement={selected}
        onEdit={() => {
          closeView();
          handleEdit(selected!);
        }}
        permission={permission}
      />

      <ModalMovementForm
        opened={debouncedIsEditFormOpened}
        closeHandler={handleCloseEdit}
        workoutMovementId={selected?.workoutMovementId}
        type='edit'
        permission={permission}
      />

      {selected && (
        <ModalMovementDelete
          opened={!!selected && deleteModalOpened}
          handleClose={closeDeleteModal}
          movement={selected}
        />
      )}
    </>
  );
};

export default MovementsTableContainer;
